import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRange, DateRangePicker, SingleInputDateRangeField  } from "@mui/x-date-pickers-pro";
import { Box, InputAdornment, IconButton } from "@mui/material";
import { isValid } from "date-fns";

//icon
import Calendar from "@mui/icons-material/Event";

//hooks
import { useLocale } from "../../../../customHooks/useLocale";

//styles
import { useStyles } from "./styles";

interface ICDateRangePickerProps {
  startDate: any,
  endDate: any,
  onChange: (start: Date, end: Date) => void;
  label?: string;
  validateDates?: boolean;
  onValidationError?: (error: string, dates: { start: any, end: any }) => void;
}


const CDateRangePicker: React.FC<ICDateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  label = 'common.forms.fields.custom_dates',
  validateDates = false,
  onValidationError,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();
  const locale = useLocale();

  const [value, setValue] = React.useState<DateRange<Date>>(() => {
    const safeStartDate = startDate && isValid(new Date(startDate)) ? new Date(startDate) : null;
    const safeEndDate = endDate && isValid(new Date(endDate)) ? new Date(endDate) : null;
    return [safeStartDate, safeEndDate];
  });

  const validateDateRange = (start: any, end: any): { isValid: boolean; error?: string } => {
    if (!validateDates) {
      return { isValid: true };
    }

    if (!start) {
      return { isValid: false, error: 'Start date is required' };
    }

    const startDate = new Date(start);
    if (!isValid(startDate)) {
      return { isValid: false, error: 'Invalid start date provided' };
    }

    // Check if start date year is less than 1970
    if (startDate.getFullYear() < 1970) {
      return { isValid: false, error: 'Start date year cannot be less than 1970' };
    }

    const endDateToCheck = end;
    const endDate = new Date(endDateToCheck);
    if (!isValid(endDate)) {
      return { isValid: false, error: 'Invalid end date provided' };
    }

    // Check if end date year is less than 1970
    if (endDate.getFullYear() < 1970) {
      return { isValid: false, error: 'End date year cannot be less than 1970' };
    }

    // Check if end date is before start date
    if (endDate.getTime() < startDate.getTime()) {
      return { isValid: false, error: 'End date cannot be before start date' };
    }

    return { isValid: true };
  };

  const handleDateChange = (newValue: DateRange<Date>) => {
    const [start, end] = newValue;

    setValue(newValue);

    // Check if both dates are present and valid
    if (start && end && isValid(start) && isValid(end)) {
      // Always check for year < 1970, regardless of validateDates setting
      if (start.getFullYear() < 1970 || end.getFullYear() < 1970) {
        console.warn('Date year is less than 1970, onChange will not be called');
        if (onValidationError) {
          const errorMsg = start.getFullYear() < 1970
            ? 'Start date year cannot be less than 1970'
            : 'End date year cannot be less than 1970';
          onValidationError(errorMsg, { start, end });
        }
        return;
      }

      // Check if end date is before start date using getTime() for reliable comparison
      if (end.getTime() < start.getTime()) {
        console.warn('End date is before start date, onChange will not be called');
        if (onValidationError) {
          onValidationError('End date cannot be before start date', { start, end });
        }
        return;
      }

      const validation = validateDateRange(start, end);

      if (!validation.isValid) {
        console.error('Date validation failed:', validation.error);
        if (onValidationError) {
          onValidationError(validation.error!, { start, end });
        }
        return;
      }

      // Call onChange when both dates are valid
      onChange(start, end);
    }
  };

  useEffect(() => {
    const safeStartDate = startDate && isValid(new Date(startDate)) ? new Date(startDate) : null;
    const safeEndDate = endDate && isValid(new Date(endDate)) ? new Date(endDate) : null;

    setValue(() => [
      safeStartDate,
      safeEndDate,
    ]);
  }, [startDate, endDate]);

  return (
    <Box className={styles.field}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale.locale}>
        <DateRangePicker
          label={t(label)}
          //autoFocus
          value={value}
          onChange={handleDateChange}
          slots={{
            field: SingleInputDateRangeField,
          }}
          slotProps={{
            textField: {
              size: 'small',
              InputProps: {
                endAdornment: <InputAdornment position="end">
                  <IconButton size="small" className={styles.iconButton} sx={{ padding: '2px' }}>
                    <Calendar color="action" />
                  </IconButton>
                </InputAdornment>,
              },
            },
            actionBar: ({ wrapperVariant }) => {
              return {
                actions: wrapperVariant === 'desktop' ? [] : ['accept'],
              }
            },
          }}
          minDate={new Date("2015-01-01T00:00")}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default CDateRangePicker;
